<template>
    <div
        v-editable="blok"
        class="hotel-product-carousel-section"
        :style="`background-color: ${blok?.BackgroundColor?.color};`"
    >
        <div class="container mx-auto">
            <h2 v-if="blok.title" class="section-title">{{ blok.title }}</h2>
            <div v-if="blok.subtitle" class="section-subtitle" v-html="blok.subtitle"></div>
            <LazyClientOnly>
                <Carousel
                    v-if="products && (products.hotels || products.parking) && getItemsToShow"
                    ref="myCarousel"
                    class="hotel-product-carousel"
                    :class="{ 'carousel-centered': productList?.length <= getItemsToShow }"
                    :items-to-show="getItemsToShow"
                >
                    <template v-if="productList && productList.length">
                        <Slide v-for="product in productList" :key="product.hotel_id">
                            <Component
                                :is="getProductType()"
                                v-if="product"
                                :blok="{ component: getProductType() }"
                                :product-data="product"
                                :airport-data="airport"
                                type="vertical"
                            />
                        </Slide>
                    </template>
                    <template #addons>
                        <navigation v-if="getItemsToShow && productList?.length > getItemsToShow">
                            <template #prev>
                                <svg
                                    v-if="config.public.STORYBLOK_FOLDER === 'apr'"
                                    width="19"
                                    height="20"
                                    viewBox="0 0 19 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0.999999 10L4.48046 6.66667L6.93031 4.32039L11.4414 4.85949e-07L14.9219 3.33333L10.4277 7.63754L17 7.63754L17 12.3463L10.4277 12.3463L14.9219 16.6667L11.4414 20L6.93031 15.6796L4.48047 13.3333L0.999999 10Z"
                                        fill="#B4E076"
                                    />
                                </svg>
                                <svg
                                    v-else-if="config.public.STORYBLOK_FOLDER === 'l4'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="34"
                                    height="34"
                                    viewBox="0 0 34 34"
                                    fill="none"
                                >
                                    <path
                                        d="M14.3084 23.8213L14.3084 18.2992L26.6227 18.2992L26.6641 15.5244L14.3084 15.5244L14.3084 10.0161L7.40582 16.9187L14.3084 23.8213Z"
                                        fill="#6B3084"
                                    />
                                </svg>
                                <NuxtIcon v-else name="ri:arrow-left-line" />
                            </template>
                            <template #next>
                                <svg
                                    v-if="config.public.STORYBLOK_FOLDER === 'apr'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="19"
                                    height="20"
                                    viewBox="0 0 19 20"
                                    fill="none"
                                >
                                    <path
                                        d="M18 10L14.5195 13.3333L12.0697 15.6796L7.55861 20L4.07814 16.6667L8.57233 12.3625H2L2 7.65372L8.57233 7.65372L4.07814 3.33333L7.55861 0L12.0697 4.32039L14.5195 6.66667L18 10Z"
                                        fill="#B4E076"
                                    />
                                </svg>
                                <svg
                                    v-else-if="config.public.STORYBLOK_FOLDER === 'l4'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="34"
                                    height="34"
                                    viewBox="0 0 34 34"
                                    fill="none"
                                >
                                    <path
                                        d="M19.6916 10.1787L19.6916 15.7008L7.37737 15.7008L7.33596 18.4756L19.6916 18.4756L19.6916 23.9839L26.5942 17.0813L19.6916 10.1787Z"
                                        fill="#6B3084"
                                    />
                                </svg>
                                <NuxtIcon v-else name="ri:arrow-right-line" />
                            </template>
                        </navigation>
                        <Pagination :paginate-by-items-to-show="false" />
                    </template>
                </Carousel>
            </LazyClientOnly>

            <div v-if="!productList?.length" class="no-products">
                Sorry, we do not have any {{ getProductTypeSimple() }} available.
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import 'vue3-carousel/dist/carousel.css';
// @ts-ignore
import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel';
import screens from '#tailwind-config/theme/screens';
import { filterProducts } from '@nuxt-monorepo/ui/utils/apiFilter';

const { blok, airport, products } = defineProps<{
    blok: any;
    airport: any;
    products: any;
}>();

const myCarousel: any = ref(null);
const config = useRuntimeConfig();

const screenSm = Number(screens.sm.slice(0, -2));
const screenLg = Number(screens.lg.slice(0, -2));
const innerWidth = ref(1000);

/** useCurrentElement used to get HTML element of carousel as this ref returns only Vue instance
 * useElementSize used to track the width of carousel and restart it if display none was applied to carousel or parent
 */
const { width: isCarouselVisible } = useElementSize(useCurrentElement(myCarousel.value));

watch(isCarouselVisible, (newValue, oldValue) => {
    // Check oldValue here to restart carousel only if width was 0 and prevent restart on resize
    if (newValue && !oldValue) {
        nextTick(() => {
            myCarousel.value?.restartCarousel();
        });
    }
});

function onResize() {
    innerWidth.value = window.innerWidth;
}

onMounted(() => {
    window.addEventListener('resize', onResize);
    onResize();
});
onUnmounted(() => window.removeEventListener('resize', onResize));

const getItemsToShow = computed(() => {
    if (innerWidth.value >= screenLg) {
        return 3;
    } else if (innerWidth.value < screenLg && innerWidth.value >= screenSm) {
        return 2;
    } else {
        return 1;
    }
    myCarousel?.value?.data.slideTo(0);
});

const productList: any = filterProducts(blok, products);

function getProductType() {
    if (blok.product_type === 'HotelWithParking' || blok.product_type === 'Hotel') {
        return 'HotelProductCard';
    } else if (blok.product_type === 'Parking') {
        return 'ParkingProductCard';
    }
}

function getProductTypeSimple() {
    if (blok.product_type === 'HotelWithParking') {
        return 'hotels';
    } else if (blok.product_type === 'Parking') {
        return 'parking lots';
    }
}
</script>

<style lang="postcss" scoped>
.hotel-product-carousel-section {
    @apply mx-auto py-16 px-4;

    .section-title {
        @apply font-normal text-center mb-6 text-2xl;
        @apply md:text-[40px];
    }
    .section-subtitle {
        @apply font-normal text-center mb-10 text-base sm:text-xl;
        :deep(a) {
            @apply text-[#26a9e0];
            &:hover {
                @apply underline;
            }
        }
        :deep(p:not(:first-of-type)) {
            @apply mt-[28px];
        }
    }

    .sb-hotel-product-card,
    .hotel-product-card {
        height: 100%;
    }

    .no-products {
        @apply text-center;
    }

    :deep(.carousel) {
        @apply text-start -mx-2.5;

        &.carousel-centered .carousel__track {
            @apply justify-center !translate-x-0;
        }

        .carousel__slide {
            @apply p-2.5;
        }

        .carousel__next,
        .carousel__prev {
            @apply h-10 w-10 text-lg rounded-full bg-white transition-all -translate-y-1/2 text-[#0094FF] mx-0;
            @apply sm:w-16 sm:h-16 sm:text-3xl top-[40%];

            box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.11);
        }
        .carousel__next {
            @apply translate-x-1 2xl:translate-x-6;
        }
        .carousel__prev {
            @apply -translate-x-1 2xl:-translate-x-6;
        }
        .carousel__next--disabled,
        .carousel__prev--disabled {
            opacity: 0;
            pointer-events: none;
        }
        .carousel__pagination {
            @apply flex sm:hidden;
        }
        .carousel__pagination-button {
            @apply w-2 h-2 rounded-full bg-gray-400 opacity-25;
        }
        .carousel__pagination-button--active,
        .carousel__pagination-button:hover {
            @apply bg-[#0094FF] opacity-75;
        }
    }
}
</style>
